import {
  Dropdown,
  DropdownItem,
  IconButton,
  Icons,
  InputField,
} from 'plume-ui';
import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { verifyNumberInput } from '../helpers';
import { CriteriaType, DisplayKeyPair, SubclauseType } from '../types';
import AffectedHrs from './AffectedHrs';
import Duration from './Duration';
import { getDefaultValueForSubCriteria } from './helpers';
import { CriteriaDisplayWithValue } from './types';
import KanbanItemHandle from 'plume-ui/dist/components/Kanban/KanbanItemHandle';

type IssueFilterItemProps = {
  filter: CriteriaDisplayWithValue;
  onDelete: () => void;
  onChange?: (data: SubclauseType | number | string) => void;
  onChangeOperator?: (operator: string) => void;
  onSelectSubItem?: (subItem: string | undefined) => void;
  allowedOperators?: DisplayKeyPair[];
  Handle?: typeof KanbanItemHandle;
};

const IssueFilterItem: FunctionComponent<IssueFilterItemProps> = ({
  filter,
  allowedOperators,
  Handle,
  onDelete,
  onChange,
  onChangeOperator,
  onSelectSubItem,
}) => {
  const { t } = useTranslation();

  const [selectedOperator, setSelectedOperator] = useState(
    filter.operator ?? '=',
  );
  const [selectedSubCriteria, setSelectedSubCriteria] = useState<
    string | undefined
  >();

  const [selectedSubItem, setSelectedSubItem] = useState<string | undefined>();

  const selectedSubCriteriaName = useMemo(() => {
    return filter.allowedSubCriterias?.find(
      (subCriteria) => subCriteria.key === selectedSubCriteria,
    )?.displayName;
  }, [selectedSubCriteria, filter.allowedSubCriterias]);

  const selectedSubItemName = useMemo(() => {
    return filter.subItems?.find((subItem) => subItem.key === selectedSubItem)
      ?.displayName;
  }, [selectedSubItem, filter.subItems]);

  const activeSubCriteria = filter.subCriterias?.[0];

  const activeAllowedSubCriteria = useMemo(
    () =>
      filter.allowedSubCriterias?.find(
        (subcriteria) => subcriteria.key === activeSubCriteria?.criteriaType,
      ),
    [selectedSubCriteria, filter.allowedSubCriterias],
  );

  const handleChangeSubCriteria = (subCriteria: string | undefined) => {
    setSelectedSubCriteria(subCriteria);
    if (subCriteria) onChange?.(getDefaultValueForSubCriteria(subCriteria));
  };

  const handleChangeSubItem = (subItem: string | undefined) => {
    setSelectedSubItem(subItem);
    onSelectSubItem?.(subItem);
  };

  useEffect(() => {
    if (filter.operator) {
      setSelectedOperator(filter.operator);
    }
    if (filter.subCriterias && Boolean(filter.subCriterias.length)) {
      setSelectedSubCriteria(filter.subCriterias[0].criteriaType);
    }
    if (filter.selectedSubItem) {
      setSelectedSubItem(filter.selectedSubItem);
    }
  }, [filter]);

  const getSelectedOperatorDisplay = () => {
    return filter.allowedOperators.find(
      (operator) => operator.key === selectedOperator,
    )?.display;
  };

  return (
    <>
      <div
        className={cx(
          'FilterItem FilterItem-issueFilter FilterConfig__section',
          {
            'FilterItem--hasSubCriteria':
              filter.allowedSubCriterias?.length ||
              filter.criteriaType === CriteriaType.HealthScore,
          },
        )}
      >
        <div className="FilterItem__column FilterItem__controlButtons">
          {Handle && (
            <Handle>
              <Icons.HamburgerIcon />
            </Handle>
          )}
          <IconButton onClick={onDelete}>
            <Icons.CrossIcon />
          </IconButton>
        </div>
        <div
          className="FilterItem__column FilterItem__name"
          data-testid="attribute"
        >
          {filter.displayName}{' '}
          {filter.criteriaType === CriteriaType.SpecificIssue &&
            Boolean(filter.subItems?.length) &&
            t('monitorAdd.dueTo')}
        </div>
        <div
          className="FilterItem__column FilterItem__criteria"
          data-testid="criteria"
        >
          {filter.criteriaType === CriteriaType.HealthScore && (
            <>
              {allowedOperators && allowedOperators.length > 1 ? (
                <Dropdown
                  label={
                    getSelectedOperatorDisplay() || t('genericLabels.select')
                  }
                  closeOnItemClick
                  openInPortal
                >
                  {allowedOperators.map((operator) => (
                    <DropdownItem
                      onClick={() => {
                        onChangeOperator?.(operator.key);
                        setSelectedOperator(operator.key);
                      }}
                      selected={selectedOperator === operator.key}
                      key={operator.key}
                    >
                      {operator?.display}
                    </DropdownItem>
                  ))}
                </Dropdown>
              ) : (
                selectedOperator
              )}
            </>
          )}
          {filter.criteriaType === CriteriaType.SpecificIssue && (
            <>
              {filter.subItems && Boolean(filter.subItems.length) && (
                <Dropdown
                  label={selectedSubItemName || t('genericLabels.any')}
                  closeOnItemClick
                  openInPortal
                >
                  <DropdownItem
                    onClick={() => handleChangeSubItem(undefined)}
                    selected={selectedSubItem === undefined}
                    key={'subItems-any'}
                  >
                    {t('genericLabels.any')}
                  </DropdownItem>
                  {filter.subItems.map((subItem) => {
                    return (
                      <DropdownItem
                        onClick={() => handleChangeSubItem(subItem.key)}
                        selected={selectedSubItem === subItem.key}
                        key={subItem.key}
                      >
                        {subItem.displayName}
                      </DropdownItem>
                    );
                  })}
                </Dropdown>
              )}
              {filter.allowedSubCriterias && (
                <div>
                  <Dropdown
                    label={
                      selectedSubCriteriaName ||
                      t('monitorAdd.noFilteringCriteria')
                    }
                    closeOnItemClick
                    openInPortal
                  >
                    <DropdownItem
                      onClick={() => handleChangeSubCriteria(undefined)}
                      selected={selectedSubCriteria === undefined}
                      key={'subCriteria-none'}
                    >
                      {t('monitorAdd.noFilteringCriteria')}
                    </DropdownItem>
                    {filter.allowedSubCriterias.map((subCriteria) => (
                      <DropdownItem
                        onClick={() => handleChangeSubCriteria(subCriteria.key)}
                        selected={selectedSubCriteria === subCriteria.key}
                        key={subCriteria.key}
                      >
                        {subCriteria.displayName}
                      </DropdownItem>
                    ))}
                  </Dropdown>

                  <div className="FilterItem__subCriteriaTooltip">
                    <Icons.CircleInfoIcon />
                    <span>
                      {
                        {
                          duration: t('monitorAdd.filterTooltips.duration'),
                          affectedHrs: t(
                            'monitorAdd.filterTooltips.affectedHrs',
                          ),
                          none: t('monitorAdd.filterTooltips.none'),
                        }[selectedSubCriteria || 'none']
                      }
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {selectedSubCriteria === 'affectedHrs' && activeSubCriteria && (
            <AffectedHrs
              allowedOperators={(
                activeAllowedSubCriteria?.allowedOperators || []
              ).map((op) => ({
                key: op.key,
                display: op.display,
              }))}
              initialData={{
                selectedValue: activeSubCriteria.selectedValue,
                criteriaType: activeSubCriteria.criteriaType,
                operator: activeSubCriteria.operator,
              }}
              setValue={(data) => {
                onChange?.(data);
              }}
            />
          )}
          {selectedSubCriteria === 'duration' && activeSubCriteria && (
            <Duration
              allowedOperators={(
                activeAllowedSubCriteria?.allowedOperators || []
              ).map((op) => ({
                key: op.key,
                display: op.display,
              }))}
              initialData={{
                selectedValue: activeSubCriteria.selectedValue,
                criteriaType: activeSubCriteria.criteriaType,
                operator: activeSubCriteria.operator,
              }}
              setValue={(data) => {
                onChange?.(data);
              }}
            />
          )}

          {filter.criteriaType === CriteriaType.HealthScore && (
            <div className="FilterItem__input">
              <InputField
                label={t('monitorAdd.healthScoreInputLabel')}
                type="number"
                value={
                  typeof filter.selectedValue === 'number'
                    ? filter.selectedValue
                    : 0
                }
                id="healthScore"
                onInput={(event: any) => {
                  const inputValue = verifyNumberInput(
                    event.target.value,
                    100,
                    0,
                  );
                  event.target.value = inputValue;
                  onChange?.(inputValue);
                }}
                max={100}
                min={0}
                maxLength={3}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default memo(IssueFilterItem);
